'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLink, type ButtonSize, type ButtonVariant } from '@kiao-voice/ui-kit'
import { ANALYTICS_EVENTS, trackEvent, type AnalyticsLocation } from '../../lib/analytics'
import { PlatformIcon } from './platform-icon'
import { ANDROID_STORE_URL, IOS_STORE_URL } from './store-urls'

const STORE_CONFIG = {
  android: {
    url: ANDROID_STORE_URL,
    ariaLabelKey: 'common:downloadAndroidAriaLabel',
    labelKey: 'common:platformAndroid',
  },
  ios: {
    url: IOS_STORE_URL,
    ariaLabelKey: 'common:downloadIosAriaLabel',
    labelKey: 'common:platformIos',
  },
} as const

export interface StoreButtonProps {
  platform: 'android' | 'ios'
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
  /** Placement label so store clicks can be compared with desktop downloads. */
  location?: AnalyticsLocation
}

function iconSize(size: ButtonSize): number {
  return size === 'compact' ? 14 : 16
}

/**
 * Mobile store CTA. Unlike {@link DownloadButton}, this component has no
 * release-manifest input: Android and iOS use stable store URLs.
 */
export function StoreButton({
  platform,
  variant = 'secondary',
  size = 'md',
  className,
  location,
}: StoreButtonProps) {
  const { t } = useTranslation(['common'])
  const store = STORE_CONFIG[platform]
  const icon = <PlatformIcon platform={platform} size={iconSize(size)} />

  return (
    <ButtonLink
      href={store.url}
      aria-label={t(store.ariaLabelKey)}
      variant={variant}
      size={size}
      className={className}
      onClick={() => {
        trackEvent(ANALYTICS_EVENTS.downloadAppClick, {
          platform,
          ...(location ? { location } : {}),
        })
      }}
    >
      {icon}
      {t(store.labelKey)}
    </ButtonLink>
  )
}
